import React, { useEffect, useState, useMemo } from 'react';
import { Clock, TrendingUp, TrendingDown, Award, AlertCircle, Layers, Package, Boxes } from 'lucide-react';
import { getFinalRankingRows, isModoAbertoFechado, computeIpcomOutcome, computeIpcomOutcomeByItem, fmtTime } from '../utils/formatters';
import { situacaoDaEtapa, SituacaoDaEtapa } from '../utils/faseDaDisputa';
import { agruparPorUnidade, ehPcpValorGlobal } from '../utils/unidadeDisputa';

interface Props {
  session: any;
}

const fmtBRL4 = (v: any) => {
  const n = typeof v === 'number' ? v : (v ? parseFloat(String(v)) : NaN);
  if (!isFinite(n)) return '—';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 4, maximumFractionDigits: 4 });
};

// Homologação removida (21/05): o ciclo da disputa termina na Seleção de
// fornecedores — homologação/adjudicação é acompanhada no módulo Monitoramento.
// 26/05: encerrada/selecao separados — gap entre fim de lances e pregoeiro
// abrir a Seleção pode durar minutos (Dispensa) ou horas/dias (Pregão).
const FASES = [
  { key: 'propostas', label: 'Propostas' },
  { key: 'disputa', label: 'Em disputa' },
  { key: 'encerrada', label: 'Encerrada' },
  { key: 'selecao', label: 'Seleção de fornecedores' },
];

/** Caracteriza o tipo da licitação: modalidade + estrutura + modo de disputa.
 *  Pregão Eletrônico (modalidadeId 5/6) ou Dispensa (8); modo Aberto (1) ou Aberto-Fechado (2);
 *  Estrutura: Item Único | Itens Múltiplos | Grupo c/ itens | Grupo + Itens (misto). */
function caracterizarLicitacao(session: any) {
  const item = session?.item || {};
  // R1.7: usa rankingData.participacao como FONTE AUTORITATIVA quando disponível
  // (vem direto do endpoint /comprasnet-fase-externa/v1/compras/{id}/participacao)
  const participacao = session?.rankingData?.participacao;
  const modalidadeId = Number(participacao?.modalidade ?? item.modalidadeId ?? item.modalidade ?? 0);
  // ModoDisputaCompraEnum (HAR 27/05/26): A=Aberto, F=Fechado, AF=Aberto/Fechado,
  // FA=Fechado/Aberto, D=Dispensa Comum. Legados: 1=Aberto, 2=Aberto-Fechado.
  const modoStr = String(participacao?.modoDisputa ?? item.modoDisputa ?? item.tipoDisputa ?? '');
  const itens = Array.isArray(session?.itemLimits) ? session.itemLimits : [];

  // Modalidade (HAR confirmou: 5=Pregão Eletrônico, 6=Dispensa Eletrônica)
  const isPregao = modalidadeId === 5 || /preg/i.test(item.modalidadeNome || '');
  const isDispensa = modalidadeId === 6 || modalidadeId === 8 || /dispens/i.test(item.modalidadeNome || '');
  let modLabel = isPregao ? 'Pregão Eletrônico' : isDispensa ? 'Dispensa de Licitação' : (item.modalidadeNome || 'Licitação');
  let modShort = isPregao ? 'PE' : isDispensa ? 'DE' : '?';
  let modColor = isPregao ? 'bg-blue-500/15 text-blue-300 border-blue-700' : isDispensa ? 'bg-purple-500/15 text-purple-300 border-purple-700' : 'bg-slate-500/15 text-slate-300 border-central-borda';

  // Estrutura — combina dados de rankingData (mais autoritativo, vem direto da API)
  // com itemLimits (fallback configurado pelo user).
  const r = session?.rankingData || {};
  const itensGrupoRaw = r.itensGrupo?.['-1'];
  const itensGrupoRows: any[] = Array.isArray(itensGrupoRaw)
    ? itensGrupoRaw
    : (itensGrupoRaw?.registros || itensGrupoRaw?.itens || itensGrupoRaw?.items || []);
  // Endpoint /em-disputa/-1/itens-grupo retorna >0 items → temos um GRUPO com itens
  // (chamada `-1` é o id do grupo agregado no portal)
  const grupoSize = itensGrupoRows.length;

  // Detalhamento endpoint (quando disponível) confirma estrutura: { tipo: 'G', qtdeItensDoGrupo: 6 }
  const det = r.detalhamentoGrupo;
  const isExplicitGroup = det?.tipo === 'G' || (det?.qtdeItensDoGrupo && det.qtdeItensDoGrupo > 0);

  // Fallback via itemLimits: aceita groupIdentificador OU groupNumber. Bug
  // 27/05/26 Pregão UFSM 90013: itemLimits gravados pelo automation.service têm
  // groupNumber=1 mas groupIdentificador vazio → detecção caía em "N Itens"
  // e groupTotals retornava null (MEU/MELHOR vazios na coleta pós-disputa).
  const itemHasGroup = (i: any) => i?.groupIdentificador != null || i?.groupNumber != null;
  const groupKeys = new Set<string>();
  itens.forEach((i: any) => {
    const k = i?.groupIdentificador ?? (i?.groupNumber != null ? `G${i.groupNumber}` : null);
    if (k) groupKeys.add(String(k));
  });
  const hasGroup = groupKeys.size > 0;
  const hasUngrouped = itens.some((i: any) => !itemHasGroup(i));

  let estLabel: string, estIcon: any;
  if (grupoSize > 0 || isExplicitGroup) {
    // Veio dos endpoints — é grupo
    const sz = grupoSize || det?.qtdeItensDoGrupo || itens.length;
    if (hasUngrouped && hasGroup) { estLabel = `Grupo + Itens (${sz} no grupo)`; estIcon = Layers; }
    else { estLabel = `Grupo c/ ${sz} itens`; estIcon = Boxes; }
  } else if (hasGroup && hasUngrouped) { estLabel = 'Grupo + Itens'; estIcon = Layers; }
  else if (hasGroup) { estLabel = `Grupo c/ ${itens.length} itens`; estIcon = Boxes; }
  else if (itens.length > 1) { estLabel = `${itens.length} Itens`; estIcon = Package; }
  else { estLabel = 'Item único'; estIcon = Package; }

  // Modo de disputa. Detecção AF via helper unificado (checa metadata + fase
  // ao vivo). modoNome mantido pros checks de Fechado/FA/D abaixo.
  const modoNome = String(item.modoDisputaNome || item.modoDisputa || '');
  const isAbertoFechado = isModoAbertoFechado(item, session?.rankingData);
  const isFechadoAberto = modoStr === 'FA' || /fechado.?aberto/i.test(modoNome);
  const isFechado = modoStr === 'F' && !isAbertoFechado && !isFechadoAberto;
  const isDispensaComum = modoStr === 'D' || /dispensa.?com.?disputa|dispensa.?comum/i.test(modoNome);
  const modoLabel = isDispensaComum ? 'Dispensa Comum'
    : isAbertoFechado ? 'Modo Aberto-Fechado'
    : isFechadoAberto ? 'Modo Fechado-Aberto'
    : isFechado ? 'Modo Fechado'
    : 'Modo Aberto';
  const modoColor = isDispensaComum ? 'bg-slate-500/15 text-slate-300 border-central-borda'
    : isAbertoFechado ? 'bg-amber-500/15 text-amber-300 border-amber-700'
    : isFechadoAberto ? 'bg-orange-500/15 text-orange-300 border-orange-700'
    : isFechado ? 'bg-rose-500/15 text-rose-300 border-rose-700'
    : 'bg-emerald-500/15 text-emerald-300 border-emerald-700';

  return { modLabel, modShort, modColor, estLabel, EstIcon: estIcon, modoLabel, modoColor };
}

/** Heurística pra deduzir fase atual a partir de rankingData / status / itens */
function deduzirFase(session: any, situacao: SituacaoDaEtapa): { key: string; label: string; etapa?: string } {
  const r = session?.rankingData || {};
  const status = session?.status;

  // 0. SELEÇÃO de fornecedores — só quando o PORTAL abriu de fato a fase:
  //    emSelecao populado, ranking final capturado (todasPropostas) ou
  //    propostasPorFornecedor. `status='finalizado'` sozinho NÃO é sinal de
  //    Seleção — a sessão finaliza no fase=E/FE e o pregoeiro pode demorar
  //    horas pra abrir (06/07 DE 109: HUB mostrava Seleção ✓ com o portal
  //    ainda em "fase de lances encerrada").
  const tp = r.todasPropostas || {};
  const temRankingFinal = Object.keys(tp).some((kk) => {
    const e = tp[kk];
    const arr = Array.isArray(e) ? e : Array.isArray(e?.items) ? e.items : [];
    return arr.length > 0;
  });
  const temPropostasPorFornecedor = r.propostasPorFornecedor && Object.keys(r.propostasPorFornecedor).length > 0;
  if (temRankingFinal || temPropostasPorFornecedor || r.emSelecao) {
    return { key: 'selecao', label: 'Seleção de fornecedores' };
  }
  // Sessão finalizada sem dado de Seleção = fim de lances, esperando o pregoeiro.
  if (status === 'finalizado') {
    return { key: 'encerrada', label: 'Disputa encerrada', etapa: 'aguardando seleção' };
  }

  // 1. ENCERRADA (fim de lances) — fase=E no portal mas pregoeiro ainda
  //    NÃO abriu a Seleção. Gap pode durar minutos (Dispensa) a horas/dias
  //    (Pregão). Step 3 explícito ajuda o user a entender o "está esperando o pregoeiro".
  const encRows = Array.isArray(r.disputaEncerrada) ? r.disputaEncerrada : (r.disputaEncerrada ? [r.disputaEncerrada] : []);
  if (encRows.length > 0 && encRows[0]?.fase === 'E') {
    return { key: 'encerrada', label: 'Disputa encerrada', etapa: 'aguardando seleção' };
  }

  // 2. Itens em disputa com fase LA/AL/LF/AF/FE/E? Mapeamento canônico
  //    FaseItemEnum (HAR 27/05/26 UFSM 90013):
  //      LA = Etapa aberta (lances livres, 15min iniciais)
  //      AL = Etapa aberta (aleatório, até 10min random pré-fechamento)
  //      LF = Etapa fechada (top-N convocados, 5min envelope sigiloso)
  //      AF = Aguardando Disputa Fechada
  //      FE = Aguardando Encerramento
  //      E  = Disputa Encerrada
  //    Grupo: itensGrupo['-1']; dispensa/pregão por item: emDisputa.
  const igRaw = r.itensGrupo?.['-1'] || r.emDisputa;
  const itensRows = Array.isArray(igRaw) ? igRaw : (igRaw?.registros || igRaw?.itens || []);
  if (itensRows.length > 0) {
    const { fase, rotulo } = situacao;
    const detalhe = itensRows[0]?.detalheSituacaoDisputaItem;
    // O rótulo depende do MODO: `LF` é envelope sigiloso em Aberto-Fechado e
    // "etapa de lances encerrada" em Aberto. A conta mora em `faseDaDisputa`,
    // com teste — ver lá o defeito do PE 14/2026 do IPHAN-BA.
    //
    // ⚠️ De propósito NÃO se usa `isModoAbertoFechado` aqui: ele aceita
    // `fase === 'LF'` como prova do modo, então perguntar a ele durante a fase
    // LF devolveria sempre "sim" e o rótulo nunca mudaria.
    //
    // ⛔ LANCES ENCERRADOS VÃO PARA O PASSO 3, mesmo com a sessão `ativo`. Até
    // 01/10/2026 todo código com rótulo caía em "Em disputa": na sessão 150 (PF,
    // PE 4/2026) o selo dizia "Aguardando encerramento" e o stepper, no passo 2,
    // dizia que ainda dava para lançar. A decisão é do `situacaoDaEtapa`, a
    // mesma que manda no selo e no botão do card.
    if (situacao.lancesEncerrados && fase !== 'E') {
      const base = rotulo || 'Etapa de lances encerrada';
      return { key: 'encerrada', label: 'Lances encerrados', etapa: detalhe ? `${base} · ${detalhe}` : base };
    }
    if (rotulo) {
      // ENCERRAMENTO ALEATÓRIO: durante a fase aberta o portal já avisa, em
      // `situacaoAposContagem='AL'`, que ao zerar a contagem entra o sorteio de até
      // 10min — ou seja, pode fechar a QUALQUER momento. O dado sempre veio no
      // payload e era descartado nas duas pontas; o HUB mostrava só "Fase aberta"
      // enquanto o portal exibia "Etapa aberta (aleatório)" (PE 15/2026, 11/08).
      // Lê da row viva e, se ela não trouxer, do itemLimits que o backend agora
      // persiste (sobrevive ao rankingData ficar velho).
      const aposContagem = itensRows[0]?.situacaoAposContagem
        ?? (session?.itemLimits || []).find((l: any) => l?.situacaoAposContagem)?.situacaoAposContagem;
      const vaiSortear = aposContagem === 'AL' && fase !== 'AL';
      const base = vaiSortear ? `${rotulo} (encerramento aleatório)` : rotulo;
      return { key: 'disputa', label: 'Em disputa', etapa: detalhe ? `${base} · ${detalhe}` : base };
    }
    if (fase === 'E') {
      return { key: 'encerrada', label: 'Disputa encerrada', etapa: 'aguardando seleção' };
    }
  }
  // 2b. Há dado de disputa fluindo (emDisputa/itens/lances) mas a fase não foi
  //     reconhecida (ex.: fase FECHADA é cega — melhorValorGeral some). Ainda é
  //     "Em disputa", não "Aguardando início" (evita o painel mostrar atrasado).
  const temDadoVivo = !!r.emDisputa || itensRows.length > 0 ||
    Object.keys(r.lancesPorParticipante || {}).length > 0 ||
    Object.keys(r.todosLances || {}).length > 0;
  if (temDadoVivo && (status === 'ativo' || status === 'aguardando' || status === 'pausado')) {
    return { key: 'disputa', label: 'Em disputa', etapa: status === 'pausado' ? 'pausado' : undefined };
  }
  // 3. Status ativo/pausado = já está em disputa (mesmo sem rankingData ainda)
  if (status === 'ativo' || status === 'pausado') {
    return { key: 'disputa', label: 'Em disputa', etapa: status === 'pausado' ? 'pausado' : 'aguardando dados' };
  }
  // 4. Aguardando = aguardando início da disputa (já configurada, no Hub)
  if (status === 'aguardando') {
    return { key: 'disputa', label: 'Aguardando início da disputa' };
  }
  // 5. Configurando = na fase de propostas (cadastro pelo Hub)
  return { key: 'propostas', label: 'Cadastro de propostas' };
}

function formatCountdown(ms: number | null): string {
  if (ms === null || !isFinite(ms) || ms <= 0) return '—';
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/**
 * Estado do Aberto-Fechado: em que etapa estamos e se o resultado final já foi
 * coletado. Existe porque as duas perguntas do header dependem disso.
 *
 * ⛔ O gatilho foi ver, em 14/09/2026 (Pederneiras 15/2026), o card afirmar
 * "✓ Você está liderando/empatado" com MEU TOTAL R$ 50.400 contra MELHOR GERAL
 * R$ 93.600 — enquanto a IPCOM estava em 3º de 17. Os dois números eram
 * verdadeiros e de MOMENTOS DIFERENTES: 50.400 era o nosso lance FECHADO,
 * 93.600 era o melhor da etapa ABERTA, que morreu quando o envelope abriu.
 *
 * Durante a etapa fechada os lances são selados: ninguém — nem o portal, nem
 * nós — sabe quem está na frente. Afirmar liderança ali é inventar.
 */
function estadoAF(session: any): {
  ehAF: boolean;
  faseItens: string | null;
  emEtapaFechada: boolean;
  resultadoColetado: boolean;
} {
  const item = session?.item || {};
  const r = session?.rankingData || {};
  const igRaw = r.itensGrupo?.['-1'] || r.emDisputa;
  const rows: any[] = Array.isArray(igRaw) ? igRaw : (igRaw?.registros || igRaw?.itens || []);
  const limits: any[] = Array.isArray(session?.itemLimits) ? session.itemLimits : [];
  // A fase vem da linha viva; quando o rankingData envelhece, o itemLimits (que
  // o backend persiste) ainda tem a última conhecida.
  const faseItens = rows[0]?.fase ?? limits.find((l) => l?.fase)?.fase ?? null;
  const f = String(faseItens || '').toUpperCase();
  return {
    ehAF: isModoAbertoFechado(item, r),
    faseItens,
    emEtapaFechada: f === 'LF' || f === 'AF' || f === 'FE',
    // Com o ranking final coletado o veredito volta a ser legítimo — foi o que
    // aconteceu na Pederneiras após "Coletar resultado": MELHOR GERAL virou
    // 35.400 e o card passou a dizer "3º de 17", corretamente.
    resultadoColetado:
      getFinalRankingRows(r).length > 0 ||
      limits.some((l) => Array.isArray(l?.allProposals) && l.allProposals.length > 0),
  };
}

export function DisputeExpandedHeader({ session }: Props) {
  // Relógio do header: o countdown e a virada de "em disputa" para "lances
  // encerrados" no instante em que a contagem zera.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const situacao = useMemo(() => situacaoDaEtapa(session, now), [session, now]);
  const fase = useMemo(() => deduzirFase(session, situacao), [session, situacao]);
  const af = useMemo(() => estadoAF(session), [session]);
  const faseIdx = FASES.findIndex((f) => f.key === fase.key);
  const tipo = useMemo(() => caracterizarLicitacao(session), [session]);
  const TipoIcon = tipo.EstIcon;

  // Staleness: durante disputa ativa, se o backend ficou sem ranking-update por
  // muito tempo (JWT frio / robô sem conexão), os valores CONGELAM e o badge
  // "pode vencer" passa a comparar com dado velho. Avisa pra o user não confiar.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (session?.status !== 'ativo' && session?.status !== 'aguardando') return;
    const t = setInterval(() => setNowMs(Date.now()), 5000);
    return () => clearInterval(t);
  }, [session?.status]);
  const lastUpdMs = session?.lastUpdate ? new Date(session.lastUpdate).getTime() : null;
  const staleSec = lastUpdMs ? Math.max(0, Math.floor((nowMs - lastUpdMs) / 1000)) : null;
  const isStale = session?.status === 'ativo' && staleSec != null && staleSec > 90;

  // Itens da disputa. GRUPO: vêm de itensGrupo['-1'] (subitens tipo "S").
  // DISPENSA / pregão por item: vêm de emDisputa (lista de itens tipo "I") —
  // que tem os mesmos campos (melhorValorGeral, melhorValorFornecedor,
  // dataHoraFimContagem, fase). Fallback garante countdown + cards em ambos.
  const itensGrupoRaw = session?.rankingData?.itensGrupo?.['-1'] || session?.rankingData?.emDisputa;
  const itensRows: any[] = useMemo(() => {
    if (!itensGrupoRaw) return [];
    if (Array.isArray(itensGrupoRaw)) return itensGrupoRaw;
    return itensGrupoRaw.registros || itensGrupoRaw.itens || itensGrupoRaw.items || [];
  }, [itensGrupoRaw]);

  // GRUPO ao vivo: o nó do grupo (emDisputa tipo 'G' / numero -1) traz o TOTAL
  // agregado do grupo já calculado pelo portal — melhorValorGeral = total
  // VENCEDOR, melhorValorFornecedor = MEU total (ambos já são totais, NÃO ×qtd).
  // É a fonte VIVA correta. Os cards somavam itensGrupo['-1'] (valores POR ITEM
  // da 1ª rodada, que NÃO atualizam) → MEU TOTAL/MELHOR GERAL travavam no total
  // inicial durante a disputa (bug UFRGS 90269 19/06). (fix 19/06)
  const liveGroupNode = useMemo(() => {
    const rows = session?.rankingData?.emDisputa as any[] | undefined;
    if (!Array.isArray(rows)) return null;
    return rows.find((r: any) => r?.tipo === 'G' || r?.numero === -1) || null;
  }, [session?.rankingData]);

  // Quantidade — pegar do itemLimits da sessão (configurado manualmente, qtd não vem do endpoint em-disputa)
  function qtdDoItem(numero: number): number {
    const lim = (session?.itemLimits || []).find((x: any) => x.itemNumber === numero);
    return Number(lim?.quantity) || 1;
  }
  // Fase Encerrada: itensGrupo não tem melhorValorGeral/melhorValorFornecedor.
  // Esses totais ficam em rankingData.disputaEncerrada[0] (header agregado do grupo).
  const encGrupo = (() => {
    const e = session?.rankingData?.disputaEncerrada;
    if (Array.isArray(e) && e.length > 0) return e[0];
    return e;
  })();
  // DISPUTA POR GRUPO: backend (P1) NÃO popula bestBid/lastBid em grupo —
  // allProposals[*] em cada item armazena o MESMO conjunto, e valor é TOTAL
  // do grupo (não unitário). Fallback lê direto desse set sem multiplicar.
  //
  // Prioridade: ranking FINAL (todasPropostas) tem `valorPropostaInicialOuLances`
  // — valor pós-disputa (ex.: IPCOM R$676.800, vencedor R$264.120 no UFSM 90013).
  // Fallback: allProposals (propostas iniciais, R$759.147 antes da disputa).
  // PCP "Por Valor Global": o lote é disputado por UM valor total (não por item).
  // O backend espelha esse valor global em bestBid/lastBid de TODOS os itens do
  // lote (mesmo groupNumber) — NÃO é unitário. Multiplicar por quantity (192)
  // inflava: 19.949 × 192 = 3,8M. Tratado como grupo (lê o total direto, sem
  // ×qtd). Hoje toda disputa PCP nossa é "Por Valor Global" (o robô só faz
  // esse modo) → portalOrigem==='pcp' basta. Se entrar PCP por-item, criar flag.
  //
  // MULTI-LOTE (08/09/2026, sessão 126): o processo tinha 2 lotes e cotamos só
  // o 2. Ler `itemLimits[0]` pegava o global do lote 1 (75.900, que nem
  // disputamos) no MELHOR GERAL e "—" no MEU TOTAL. O valor global vem dos
  // itens do(s) lote(s) COTADO(s) — Σ(piso × qtd) > 0 — via agruparPorUnidade.
  const isPcpGlobal = ehPcpValorGlobal(session);
  // Unidades derivadas dos itemLimits — cobre os TRÊS formatos: grupo/lote,
  // item solto (dispensa e pregão por item) e misto. É o caminho do ComprasNet
  // e do PCP, que não têm resultData.lotes. No PCP global, meuTotal/melhorTotal
  // de cada unidade é o valor global do lote (sem × qtd).
  const unidades = useMemo(
    () => agruparPorUnidade(session?.itemLimits || [], { valorGlobal: isPcpGlobal }),
    [session?.itemLimits, isPcpGlobal],
  );
  const unidadesCotadas = useMemo(() => unidades.filter((u) => !u.naoCotada), [unidades]);
  // PCP global: total dos lotes COTADOS. Com 1 lote cotado é o valor dele; com
  // vários, a soma só vale quando todos têm o valor (senão a comparação
  // MEU × MELHOR misturaria lotes com e sem dado).
  const pcpGlobal = useMemo(() => {
    const soma = (campo: 'meuTotal' | 'melhorTotal'): number | null => {
      if (!isPcpGlobal || unidadesCotadas.length === 0) return null;
      const vals = unidadesCotadas.map((u) => u[campo]);
      if (vals.some((v) => v == null || !(v > 0))) return null;
      return (vals as number[]).reduce((a, b) => a + b, 0);
    };
    return { meu: soma('meuTotal'), melhor: soma('melhorTotal') };
  }, [unidadesCotadas, isPcpGlobal]);
  const groupTotals = useMemo(() => {
    const isGroup = tipo.estLabel.startsWith('Grupo') || isPcpGlobal;
    if (!isGroup) return null;
    const IPCOM_CNPJ = '06163889000104';

    // 1ª: ranking final (todasPropostas — pós-lances)
    const finalRows = getFinalRankingRows(session?.rankingData);
    if (finalRows.length > 0) {
      const getVal = (r: any) => {
        const v = r?.valores?.valorPropostaInicialOuLances?.valorCalculado?.valorTotal
          ?? r?.valores?.valorPropostaInicialOuLances?.valorInformado
          ?? r?.valores?.valorPropostaInicial?.valorCalculado?.valorTotal
          ?? r?.valores?.valorPropostaInicial?.valorInformado;
        return typeof v === 'number' && isFinite(v) && v > 0 ? v : null;
      };
      // Desclassificada NÃO conta pro "melhor geral" (08/07, PE 90004): antes o
      // Math.min pegava o menor total INCLUINDO propostas desclassificadas — o
      // card mostrava R$ 5.025 (S&N, desclassificada) em vez do melhor VÁLIDO.
      const isDescl = (r: any): boolean => {
        const s = r?.situacao != null ? String(r.situacao) : '';
        return s === '2' || s === '3' || s === '5'
          || !!(r?.motivoDesclassificacao && String(r.motivoDesclassificacao).trim());
      };
      const mineRow = finalRows.find((r: any) => r?.participante?.identificacao === IPCOM_CNPJ);
      const mineVal = mineRow ? getVal(mineRow) : null;
      const allVals = finalRows.filter((r: any) => !isDescl(r)).map(getVal).filter((v): v is number => v != null);
      const bestVal = allVals.length ? Math.min(...allVals) : null;
      if (mineVal != null || bestVal != null) {
        return { meuTotal: mineVal, melhorTotal: bestVal };
      }
    }

    // 2ª: allProposals (propostas iniciais, replicadas por item em grupo)
    const limits: any[] = (session?.itemLimits as any[]) || [];
    const apEntry = limits.find((il: any) => Array.isArray(il?.allProposals) && il.allProposals.length > 0);
    if (!apEntry) return null;
    const ap: any[] = apEntry.allProposals;
    const mine = ap.find((x) => x.ehMinha);
    const mineVal = mine ? (Number(mine.valorNegociado ?? mine.valorOfertado) || null) : null;
    const all = ap
      .map((x) => Number(x.valorNegociado ?? x.valorOfertado))
      .filter((v) => isFinite(v) && v > 0);
    const bestVal = all.length ? Math.min(...all) : null;
    return { meuTotal: mineVal, melhorTotal: bestVal };
  }, [session?.itemLimits, session?.rankingData, tipo.estLabel, isPcpGlobal]);
  const isGroup = tipo.estLabel.startsWith('Grupo') || isPcpGlobal;
  const meuTotal = useMemo(() => {
    // PCP "Por Valor Global": valor global = itemLimits[0].lastBid (já é o total
    // do lote, NÃO ×qtd). Ao vivo vem do poll; pós-coleta via todasPropostas.
    if (isPcpGlobal) {
      if (pcpGlobal.meu != null) return pcpGlobal.meu;
      return groupTotals?.meuTotal ?? null;
    }
    // 1ª prioridade: soma do MEU lance (lastBid) de cada item × qtd — autoritativo,
    // atualizado pela disputa E pelo ranking. Pós-coleta o emDisputa fica vazio.
    // PULA em GRUPO: lastBid em grupo guarda o TOTAL replicado por item, então
    // multiplicar por qty infla bilhões (Bug 5 27/05/26 UFSM 90013: 30012 × 759k = 22B).
    const limits: any[] = (session?.itemLimits as any[]) || [];
    if (!isGroup) {
      const fromLimits = limits.reduce((sum, il) => (il?.lastBid != null ? sum + Number(il.lastBid) * (il.quantity || 1) : sum), 0);
      if (fromLimits > 0) return fromLimits;
    }
    // GRUPO pós-coleta: o total autoritativo é o do IPCOM no RANKING FINAL coletado
    // (groupTotals lê todasPropostas), NÃO a soma do itensGrupo ao vivo (linha 3ª),
    // que fica velha se o poll per-item parou (incidente 15/06: card travava no
    // total inicial 135.865 mesmo após coletar o final 131.503). Só vale quando há
    // ranking coletado (finalRows) — ao vivo cai na soma do itensGrupo abaixo.
    if (isGroup && groupTotals?.meuTotal != null && getFinalRankingRows(session?.rankingData).length > 0) {
      return groupTotals.meuTotal;
    }
    // GRUPO ao vivo: total do grupo direto do nó 'G' (já é o MEU total agregado).
    // Vem ANTES da soma do itensGrupo (3ª), que usa valores por-item da 1ª rodada
    // e fica congelada durante a disputa.
    if (isGroup && liveGroupNode) {
      const v = liveGroupNode.melhorValorFornecedor?.valorCalculado ?? liveGroupNode.melhorValorFornecedor?.valorInformado;
      if (typeof v === 'number' && v > 0) return v;
    }
    // 2ª: valor agregado do disputaEncerrada
    const direct = encGrupo?.melhorValorFornecedor?.valorCalculado ?? encGrupo?.melhorValorFornecedor?.valorInformado;
    if (typeof direct === 'number') return direct;
    // 3ª: soma dos unitários do itensGrupo × qtd
    if (itensRows.length > 0) {
      return itensRows.reduce((sum, r) => {
        const u = r.melhorValorFornecedor?.valorCalculado ?? r.melhorValorFornecedor?.valorInformado ?? 0;
        return sum + (u * qtdDoItem(r.numero));
      }, 0);
    }
    // 4ª: grupo pós-coleta — allProposals tem o set replicado com valor TOTAL.
    return groupTotals?.meuTotal ?? null;
  }, [itensRows, session?.itemLimits, session?.rankingData, encGrupo, groupTotals, isGroup, isPcpGlobal, liveGroupNode, pcpGlobal]);
  const melhorTotal = useMemo(() => {
    // PCP "Por Valor Global": melhor global = itemLimits[0].bestBid (total do lote,
    // NÃO ×qtd) ou o menor de todasPropostas pós-coleta.
    if (isPcpGlobal) {
      // Só o(s) lote(s) cotado(s): o ranking coletado (todasPropostas '-1') já
      // é do lote alvo, então entra como candidato junto do vivo.
      const live = pcpGlobal.melhor;
      const cands = [live, groupTotals?.melhorTotal ?? null].filter(
        (v): v is number => typeof v === 'number' && v > 0,
      );
      return cands.length ? Math.min(...cands) : null;
    }
    // Valor do ESTADO da disputa (emDisputa/disputaEncerrada) — pode estar velho
    // pós-disputa (o bot parou de capturar quando perdeu a liderança).
    // GRUPO ao vivo: o nó 'G' traz melhorValorGeral = total VENCEDOR (já agregado).
    // Tem prioridade sobre a soma do itensGrupo (valores por-item da 1ª rodada,
    // congelados durante a disputa → travavam o MELHOR GERAL).
    const liveGroupBest = isGroup && liveGroupNode
      ? (liveGroupNode.melhorValorGeral?.valorCalculado ?? liveGroupNode.melhorValorGeral?.valorInformado)
      : null;
    const directE = encGrupo?.melhorValorGeral?.valorCalculado ?? encGrupo?.melhorValorGeral?.valorInformado;
    const fromState = (typeof liveGroupBest === 'number' && liveGroupBest > 0)
      ? liveGroupBest
      : typeof directE === 'number'
        ? directE
        : itensRows.length > 0
          ? itensRows.reduce((sum, r) => sum + ((r.melhorValorGeral?.valorCalculado ?? r.melhorValorGeral?.valorInformado ?? 0) * qtdDoItem(r.numero)), 0)
          : null;
    // Valor AUTORITATIVO: itemLimits[].bestBid (atualizado pela disputa E pelo
    // ranking final capturado). Pós-disputa, é o melhor de verdade (ex.: vencedor).
    // PULA em GRUPO (mesmo motivo de Bug 5) — usa o agregado via groupTotals.
    const limits: any[] = (session?.itemLimits as any[]) || [];
    const fromLimits = !isGroup
      ? limits.reduce((sum, il) => (il?.bestBid != null ? sum + Number(il.bestBid) * (il.quantity || 1) : sum), 0)
      : 0;
    // O MELHOR é o MENOR valor — pega o menor entre estado, bestBid e (grupo)
    // o total agregado via allProposals.
    const cands = [
      fromState,
      fromLimits > 0 ? fromLimits : null,
      groupTotals?.melhorTotal ?? null,
    ].filter((v): v is number => typeof v === 'number' && v > 0);
    return cands.length ? Math.min(...cands) : null;
  }, [itensRows, session?.itemLimits, encGrupo, groupTotals, isGroup, isPcpGlobal, liveGroupNode, pcpGlobal]);
  const limiteTotal = useMemo(() => {
    // Soma de limitValue × quantity de cada item — referência fixa do que foi
    // cadastrado pra essa licitação. Permite validar a soma proposta antes da
    // disputa rolar (ex.: confirmar que "Meu valor" do portal bate com o total
    // configurado). Independente de lance corrente.
    const limits: any[] = (session?.itemLimits as any[]) || [];
    if (limits.length === 0) return null;
    const sum = limits.reduce((acc, il) => {
      const v = Number(il?.limitValue);
      const q = Number(il?.quantity) || 1;
      return isFinite(v) && v > 0 ? acc + v * q : acc;
    }, 0);
    return sum > 0 ? sum : null;
  }, [session?.itemLimits]);
  const delta = meuTotal != null && melhorTotal != null && melhorTotal > 0
    ? ((meuTotal - melhorTotal) / melhorTotal) * 100
    : null;

  // Tempo restante — countdown LIVE (recalcula a cada segundo).
  // Pra pregão por item múltiplo cada item tem fimContagem próprio; o header
  // mostra o próximo a encerrar e identifica qual item é.
  const fimContagemInfo = useMemo(() => {
    // Candidatos: rankingData (só popula no "Coletar resultado") +
    // itemLimits[].fimContagem (fonte VIVA durante a disputa, atualizada a cada
    // lance — é o reset de prorrogação pra 2 min). Sem itemLimits o countdown
    // ficava sem dado e o header mostrava "Em disputa" estático.
    const cand: Array<{ ts: number; itemNumber: number | null }> = [];
    for (const r of itensRows) {
      if (r?.dataHoraFimContagem) cand.push({ ts: new Date(r.dataHoraFimContagem).getTime(), itemNumber: r.numero ?? null });
    }
    for (const lim of (session?.itemLimits || []) as any[]) {
      if (lim?.fimContagem) cand.push({ ts: new Date(lim.fimContagem).getTime(), itemNumber: lim.itemNumber ?? null });
    }
    const valid = cand.filter((c) => isFinite(c.ts));
    if (valid.length === 0) return null;
    const countAtivos = valid.filter((c) => c.ts > Date.now()).length;
    let future = valid.filter((c) => c.ts > Date.now());

    // DENTRO de uma unidade todos os itens encerram JUNTOS, então o menor não é
    // "o próximo a encerrar" — é o item que ainda não recebeu o reset. Em
    // PRORROGAÇÃO o portal empurra o fim a cada lance, e o update nem sempre
    // chega em todos os itens no mesmo tick: o menor vira um valor velho e o
    // header adianta o relógio.
    //
    // Medido no PE 22/2026 COREN-PE (20/08): HUB marcando 00:27 com o portal em
    // 01:12, e "1ª rodada" enquanto o portal dizia PRORROGAÇÃO.
    //
    // Regra: o MAIOR dentro da unidade (o reset mais recente) e o MENOR ENTRE
    // unidades (aí sim, a próxima a encerrar). Em pregão por item cada item é
    // sua própria unidade e o comportamento não muda.
    const chaveDoItem = new Map<number, string>();
    for (const lim of (session?.itemLimits || []) as any[]) {
      const g = lim?.groupNumber ?? lim?.groupIdentificador ?? null;
      if (lim?.itemNumber != null) {
        chaveDoItem.set(Number(lim.itemNumber), g == null || g === '' ? `item-${lim.itemNumber}` : `g-${g}`);
      }
    }
    if (future.length > 1 && chaveDoItem.size > 0) {
      const porUnidade = new Map<string, { ts: number; itemNumber: number | null }>();
      for (const c of future) {
        const k = c.itemNumber != null ? (chaveDoItem.get(Number(c.itemNumber)) || `item-${c.itemNumber}`) : 'sem-item';
        const atual = porUnidade.get(k);
        if (!atual || c.ts > atual.ts) porUnidade.set(k, c);
      }
      future = Array.from(porUnidade.values());
    }

    // menor no futuro (próxima unidade a encerrar); se todos passaram, o mais recente.
    const pick = (future.length > 0 ? future : valid).reduce((a, b) =>
      future.length > 0 ? (b.ts < a.ts ? b : a) : (b.ts > a.ts ? b : a));
    return { ts: pick.ts, itemNumber: pick.itemNumber, countAtivos };
  }, [itensRows, session?.itemLimits]);
  const fimContagemTs = fimContagemInfo?.ts ?? null;
  const tempoRestanteMs = fimContagemTs ? fimContagemTs - now : null;
  // Grupo: todos os itens têm mesmo fimContagem; não vale mostrar "Item X" pois é redundante.
  const ehGrupo = tipo.estLabel.startsWith('Grupo');
  const mostrarItemNoCountdown = !ehGrupo && (fimContagemInfo?.countAtivos ?? 0) > 1 && fimContagemInfo?.itemNumber != null;

  // Minha classificação (do ranking final, se disponível). Usa o helper porque
  // todasPropostas vem em 3 shapes (HAR UFSM 90013): { '-1': {items} },
  // { '-1': [{propostasItem}] } ou {'1','2',...: [{propostasItem}]} replicado.
  const rankingRows = getFinalRankingRows(session?.rankingData);
  // Fonte ÚNICA (computeIpcomOutcome) — deriva a posição por valor quando a
  // `classificacao` vem vazia (desclassificado/não convocado, ex.: DEFENSORIA
  // 90007). Mesma lógica do card e da lista, pra não divergir entre as telas.
  const outcome = computeIpcomOutcome(session?.rankingData);
  const minhaClassif = outcome.classif;
  const classifDerivada = outcome.derived;
  const totalParticipantes = outcome.total;
  // Posição POR ITEM (dispensa/pregão por item): cada item tem ranking/vencedor
  // próprios → o "Xº de N" único do header não faz sentido (ex.: 2º no item 1,
  // 1º no item 2). Mostra uma linha de posições por item; grupo segue no agregado.
  const outcomesByItem = computeIpcomOutcomeByItem(session?.rankingData);
  const isPerItemRanking = !isGroup && outcomesByItem.length > 1;

  // ── BLL/BNC multi-lote ──────────────────────────────────────────────────
  // Cada lote é uma competição própria. O `computeIpcomOutcome` acima deriva do
  // rankingData, que numa sessão BLL guarda o ranking de UM lote — por isso o
  // header exibia "1º de 11" e "Meu Total R$ 12,00" (lote 1) numa licitação em
  // que ganhamos 1 de 3 lotes e ficamos em 2º no geral.
  const lotesBll: any[] = Array.isArray(session?.resultData?.lotes) ? session.resultData.lotes : [];
  const globalBll: any[] = Array.isArray(session?.resultData?.classificacaoGlobal)
    ? session.resultData.classificacaoGlobal
    : [];
  // `unidades`/`unidadesCotadas` calculados lá em cima (junto do isPcpGlobal).
  const multiUnidade = unidades.length > 1;
  const multiLote = lotesBll.length > 1;
  const ehIpcomNome = (n: string) => /IPCOM\s+TECNOLOGIA/i.test(String(n || ''));
  const minhaGlobal = globalBll.find((g: any) => ehIpcomNome(g.name)) || null;
  // Quantidade da unidade, pra converter lance unitário em total do lote.
  const qtdDoLote = (num: any): number =>
    (session?.itemLimits || [])
      .filter((il: any) => Number(il.groupNumber) === Number(num))
      .reduce((acc: number, il: any) => acc + (Number(il.quantity) || 0), 0);
  // Com julgamento global a posição/total do header passam a ser os da SOMA —
  // a única classificação que existe nesse edital. Sem ele, não há posição
  // única: o header some com o "Xº de N" em vez de mostrar a de um lote só.
  const classifExibida = multiLote ? (minhaGlobal ? minhaGlobal.position : null) : minhaClassif;
  const totalExibido = multiLote ? (minhaGlobal ? globalBll.length : 0) : totalParticipantes;
  const meuTotalExibido = multiLote && minhaGlobal ? minhaGlobal.total : meuTotal;
  // Linha da IPCOM no ranking (pro alerta de negociação ativa abaixo).
  const minhaProp = rankingRows.find(
    (r: any) => String(r?.participante?.identificacao || '').replace(/\D/g, '') === '06163889000104',
  );

  return (
    <div className="bg-central-cartao border border-central-borda rounded-lg p-5 mb-4">
      {/* R1.10: tag bar removida daqui — já está no DisputeSessionCard (card colapsado) */}

      {/* Fase progress */}
      <div className="flex items-center justify-between mb-5 px-2">
        {FASES.map((f, idx) => {
          // Seleção é o estágio TERMINAL do módulo Disputar (homologação vive no
          // Monitorar) — quando a sessão chega nela, o ciclo aqui está completo:
          // mostra ✓ verde em vez de "4 em andamento" (pedido do user 06/07).
          const isTerminalDone = idx === faseIdx && f.key === 'selecao';
          const isActive = idx === faseIdx && !isTerminalDone;
          const isPast = idx < faseIdx || isTerminalDone;
          return (
            <React.Fragment key={f.key}>
              <div className="flex flex-col items-center">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold border-2 ${
                  isActive ? 'bg-blue-500 border-blue-400 text-white' :
                  isPast ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400' :
                  'bg-slate-700 border-central-borda text-slate-500'
                }`}>
                  {isPast ? '✓' : idx + 1}
                </div>
                <span className={`text-xs mt-1.5 ${isActive ? 'text-blue-400 font-medium' : isPast ? 'text-emerald-400' : 'text-slate-500'}`}>
                  {idx === faseIdx ? fase.label : f.label}
                </span>
              </div>
              {idx < FASES.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 ${idx < faseIdx ? 'bg-emerald-500/50' : 'bg-slate-700'}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* 4 Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {/* Tempo / Status */}
        <div className="bg-central-elevado rounded-lg p-4 border border-central-borda">
          <div className="flex items-center gap-2 text-xs uppercase text-slate-500 tracking-wide mb-1">
            <Clock size={14} />
            {fase.key === 'disputa' ? 'Tempo Restante'
              : fase.key === 'encerrada' && situacao.encerradaEm != null ? 'Etapa de lances'
              : 'Status'}
          </div>
          {fase.key === 'encerrada' && situacao.encerradaEm != null ? (
            /* Lances encerrados: no lugar do relógio, a hora em que acabou. Uma
               caixa de "tempo restante" sem contagem sugere disputa viva onde
               não há (sessão 150, 01/10/2026). */
            <>
              <div className="text-2xl font-bold text-slate-200">
                Encerrada às <span className="font-mono">{fmtTime(situacao.encerradaEm)}</span>
              </div>
              {fase.etapa && <div className="text-xs text-slate-400 mt-1">{fase.etapa}</div>}
            </>
          ) : fase.key === 'disputa' && tempoRestanteMs !== null && tempoRestanteMs > 0 ? (
            <>
              <div className="text-3xl font-mono font-bold text-amber-400">{formatCountdown(tempoRestanteMs)}</div>
              <div className="text-xs text-slate-400 mt-1">
                {mostrarItemNoCountdown
                  ? <>próximo: <span className="text-amber-300 font-medium">Item {fimContagemInfo!.itemNumber}</span> · {fimContagemInfo!.countAtivos} ativos</>
                  : (fase.etapa || 'Disputa aberta')}
              </div>
            </>
          ) : (
            <>
              <div className="text-2xl font-bold text-slate-200">{fase.label}</div>
              {fase.etapa && <div className="text-xs text-slate-400 mt-1">{fase.etapa}</div>}
            </>
          )}
        </div>

        {/* Melhor Possível (renomeado de "Limite Total" — soma dos pisos × qtd
            = o mínimo que VOCÊ aceita ofertar). Badge "pode vencer?" compara
            com MELHOR GERAL: se piso ≤ melhor do mercado, ainda dá pra cobrir;
            senão, vencedor já está abaixo do seu piso. */}
        <div className={`rounded-lg p-4 border ${
          limiteTotal != null && melhorTotal != null && melhorTotal > 0
            ? (limiteTotal <= melhorTotal
                ? 'bg-emerald-500/5 border-emerald-700/50'
                : 'bg-rose-500/5 border-rose-700/50')
            : 'bg-central-elevado border-central-borda'
        }`}>
          <div className="flex items-center justify-between gap-2 text-xs uppercase text-slate-500 tracking-wide mb-1">
            <span className="flex items-center gap-2">
              <TrendingDown size={14} className="text-slate-400" />
              Melhor possível
            </span>
            {isStale ? (
              <span
                className="text-[10px] px-1.5 py-0.5 rounded border font-medium bg-amber-500/15 text-amber-300 border-amber-700/40"
                title={`O robô não atualiza os valores há ${staleSec}s (provável JWT frio / sem conexão). O "pode vencer" abaixo compara com dado VELHO — não confie. Veja o valor real no portal.`}
              >
                ⏱ desatualizado há {staleSec}s
              </span>
            ) : limiteTotal != null && melhorTotal != null && melhorTotal > 0 && (
              <span
                className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${
                  limiteTotal <= melhorTotal
                    ? 'bg-emerald-500/15 text-emerald-300 border-emerald-700/40'
                    : 'bg-rose-500/15 text-rose-300 border-rose-700/40'
                }`}
                title={limiteTotal <= melhorTotal
                  ? 'Seu piso permite chegar ao valor do vencedor (ou abaixo). Vencer é possível.'
                  : `Vencedor atual está R$ ${(limiteTotal - melhorTotal).toFixed(4)} ABAIXO do seu piso. Pra vencer, precisa baixar pisos.`}
              >
                {limiteTotal <= melhorTotal ? '✓ pode vencer' : '⚠ piso alto'}
              </span>
            )}
          </div>
          <div className="text-3xl font-mono font-bold text-slate-300">{fmtBRL4(limiteTotal)}</div>
          <div className="text-xs text-slate-500 mt-1">
            {isStale
              ? <span className="text-amber-400">⚠ valores congelados há {staleSec}s — confira no portal</span>
              : unidadesCotadas.length > 0 && unidadesCotadas.length < unidades.length
                // Deixa explícito o RECORTE. O número sempre ignorou piso 0, então
                // já excluía as unidades não cotadas — mas o rótulo "tudo" dava a
                // entender que somava a licitação inteira, e no PE 90004 a IPCOM
                // cotou 1 de 3 grupos.
                ? `Se baixar ao piso ${unidadesCotadas.length === 1 ? 'a unidade cotada' : `as ${unidadesCotadas.length} unidades cotadas`} (qtd × piso)`
                : 'Se baixar tudo ao piso (qtd × piso)'}
          </div>
        </div>

        {/* Meu total */}
        <div className={`rounded-lg p-4 border ${classifExibida === 1 ? 'bg-emerald-500/10 border-emerald-700' : 'bg-central-elevado border-central-borda'}`}>
          <div className="flex items-center justify-between text-xs uppercase text-slate-500 tracking-wide mb-1">
            <span className="flex items-center gap-2">
              {classifExibida === 1 ? <Award size={14} className="text-amber-400" /> : <TrendingDown size={14} />}
              Meu Total
            </span>
            {!isPerItemRanking && classifExibida && totalExibido > 0 && (
              <span
                className={`text-xs ${classifExibida === 1 ? 'text-amber-400 font-bold' : 'text-slate-400'}`}
                title={
                  multiLote
                    ? 'Classificação pela SOMA dos lotes (menor preço global), como o edital manda.'
                    : (classifDerivada ? 'Sem classificação oficial do portal (desclassificado/não convocado) — posição derivada pela ordem do valor final.' : undefined)
                }
              >
                {classifExibida}º de {totalExibido}{!multiLote && classifDerivada ? '*' : ''}
              </span>
            )}
            {/* Multi-lote SEM critério global: não existe posição única. Dizer
                "1º" com o número de um lote é o erro que essa licitação expôs. */}
            {multiLote && !minhaGlobal && (
              <span className="text-xs text-slate-500" title="Cada lote tem classificação própria. Marque o julgamento como Global nas configurações se o edital somar os lotes.">
                {lotesBll.length} lotes
              </span>
            )}
          </div>
          <div className={`text-3xl font-mono font-bold ${classifExibida === 1 ? 'text-emerald-300' : 'text-slate-100'}`}>
            {fmtBRL4(meuTotalExibido)}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {multiLote && minhaGlobal
              ? `Soma dos ${lotesBll.length} lotes (valor × qtd)`
              : isPcpGlobal
                ? (unidadesCotadas.length > 1
                    ? `Σ do meu valor global nos ${unidadesCotadas.length} lotes cotados`
                    : `Meu valor global do lote${unidadesCotadas[0]?.grupo != null ? ` ${unidadesCotadas[0].grupo}` : ''} (12 meses)`)
                : 'Soma dos itens (qtd × meu lance)'}
          </div>
        </div>

        {/* Melhor total + delta */}
        <div className="bg-central-elevado rounded-lg p-4 border border-central-borda">
          <div className="flex items-center gap-2 text-xs uppercase text-slate-500 tracking-wide mb-1">
            <TrendingUp size={14} />
            Melhor Geral
          </div>
          <div className="text-3xl font-mono font-bold text-blue-300">{fmtBRL4(melhorTotal)}</div>
          {isPcpGlobal && melhorTotal != null && (
            <div className="text-[10px] text-slate-500">
              {unidadesCotadas.length > 1
                ? `Σ do melhor valor global nos ${unidadesCotadas.length} lotes cotados`
                : `Melhor valor global do lote${unidadesCotadas[0]?.grupo != null ? ` ${unidadesCotadas[0].grupo}` : ''}`}
            </div>
          )}
          {isPerItemRanking ? (() => {
            // POR ITEM: veredito agregado ("ATRÁS") contradiz a realidade (você pode
            // ter ganho um item e perdido outro). Mostra o resultado por item.
            const ganhos = outcomesByItem.filter((o) => o.isWinner).length;
            const n = outcomesByItem.length;
            const todos = ganhos === n;
            return (
              <div className={`text-xs mt-1 ${ganhos > 0 ? (todos ? 'text-emerald-400' : 'text-amber-400') : 'text-amber-400'}`}>
                {ganhos > 0
                  ? `${todos ? '✓' : '◐'} Ganhou ${ganhos} de ${n} ${n === 1 ? 'item' : 'itens'}`
                  : `⚠ Não venceu nenhum item`}
              </div>
            );
          })() : af.emEtapaFechada && !af.resultadoColetado ? (
            /* Etapa fechada sem resultado coletado: o MELHOR GERAL exibido é o
               da etapa ABERTA e o nosso valor já é o do envelope. Comparar os
               dois afirma liderança que ninguém pode saber. Diz o que se sabe. */
            <div className="text-xs mt-1 text-slate-400 leading-snug">
              🔒 Etapa fechada — os lances são selados e o melhor acima é o da etapa
              <strong className="text-slate-300"> aberta</strong>. A posição só existe
              depois de <strong className="text-slate-300">Coletar resultado</strong>.
            </div>
          ) : melhorTotal > 0 && meuTotal != null && (() => {
            // melhorTotal é o MENOR (melhor) total do mercado. Se meuTotal > melhorTotal,
            // alguém está na frente → estamos ATRÁS (independente de quão pouco). Empate
            // exato (meu == melhor) = liderando. Threshold de 0,5 centavo só p/ float.
            // SÓ avalia se meuTotal é conhecido (senão não dá pra afirmar liderança).
            const diff = meuTotal - melhorTotal;
            const atras = diff > 0.005;
            return (
              <div className={`text-xs mt-1 ${atras ? 'text-amber-400' : 'text-emerald-400'}`}>
                {atras
                  ? `⚠ Você está ATRÁS — ${fmtBRL4(diff)} acima do melhor${minhaClassif && minhaClassif > 1 ? ` (${minhaClassif}º de ${totalParticipantes})` : ''}`
                  : '✓ Você está liderando/empatado'}
              </div>
            );
          })()}

          {/* FOLGA ATÉ O CORTE — o número que decide a etapa aberta do AF.
              ⛔ Ele não existia na tela. Em 14/09/2026 a UFC PE 80/2026 foi
              perdida por R$ 0,00012 de folga: o robô ficou 7 minutos colado na
              borda do corte e não havia como ver. MELHOR POSSÍVEL, MEU TOTAL e
              MELHOR GERAL estavam todos certos, e nenhum respondia "quanto
              falta para eu cair fora".
              Na etapa aberta do AF a disputa não é pelo 1º lugar — é por ficar
              entre os até 10% acima do melhor, que são os convocados. */}
          {af.ehAF && !af.emEtapaFechada && !af.resultadoColetado
            && melhorTotal > 0 && meuTotal != null && (() => {
            const corte = melhorTotal * 1.1;
            const folga = corte - meuTotal;
            const dentro = folga >= 0;
            // Mesma margem que o robô passou a respeitar (0,01% do corte):
            // abaixo dela, uma melhora de centavos do líder nos derruba.
            const margem = Math.max(0.01, corte * 0.0001);
            const apertado = dentro && folga < margem;
            return (
              <div
                className={`text-xs mt-2 pt-2 border-t border-central-borda leading-snug ${
                  !dentro ? 'text-red-400' : apertado ? 'text-amber-300' : 'text-slate-400'
                }`}
                title={`Corte de 10% sobre o melhor (${fmtBRL4(melhorTotal)}) = ${fmtBRL4(corte)}.\nQuem fica até esse valor é convocado para o lance fechado (Lei 14.133 art. 56 §1º).`}
              >
                {!dentro ? (
                  <>⚠ <strong>FORA do corte</strong> por {fmtBRL4(-folga)} — sem convocação para o lance fechado</>
                ) : apertado ? (
                  <>⚠ <strong>Na borda do corte</strong> — folga de apenas {fmtBRL4(folga)}; qualquer melhora do líder derruba</>
                ) : (
                  <>Dentro do corte, folga de <strong className="text-slate-300">{fmtBRL4(folga)}</strong> (corte: {fmtBRL4(corte)})</>
                )}
              </div>
            );
          })()}
        </div>
      </div>

      {/*
        BLL/BNC MULTI-LOTE: um bloco por lote. Cada lote é uma competição
        própria, com melhor lance, nosso lance e posição próprios — os 4 cards
        acima só conseguem falar de um número agregado.

        No PE 021/2026 (19/08) os três lotes apareciam com os MESMOS valores
        (R$ 12,00, do lote 1) porque a coleta carimbava o resultado de um lote
        em todos. Corrigido no backend; aqui é a leitura por lote.
      */}
      {multiLote && (
        <div className="mt-3 space-y-2">
          <div className="text-xs uppercase text-slate-500 tracking-wide">
            Por lote
            <span className="normal-case text-slate-600 ml-2">
              {minhaGlobal
                ? 'cada lote decide o seu; a classificação da licitação é a soma'
                : 'cada lote tem vencedor próprio'}
            </span>
          </div>
          {lotesBll.map((l: any) => {
            const minha = (l.competitors || []).find((c: any) => ehIpcomNome(c.name));
            const pos = minha?.position ?? l.position ?? null;
            const venceu = pos === 1;
            const qtd = qtdDoLote(l.lote);
            const meuUnit = l.meuLance ?? minha?.totalValue ?? null;
            const melhorUnit = l.melhorLance ?? null;
            const atras = meuUnit != null && melhorUnit != null && meuUnit - melhorUnit > 0.005;
            // Piso configurado do lote — o mesmo número que o robô respeita.
            const pisoLote = (session?.itemLimits || [])
              .filter((il: any) => Number(il.groupNumber) === Number(l.lote))
              .reduce((acc: number, il: any) => acc + (Number(il.limitValue) || 0), 0);
            return (
              <div
                key={`hl-${l.lote}`}
                className={`rounded-lg border px-3 py-2.5 ${
                  venceu ? 'bg-emerald-500/5 border-emerald-700/50' : 'bg-central-elevado border-central-borda'
                }`}
              >
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 min-w-[120px]">
                    {venceu ? <Award size={14} className="text-amber-400" /> : <TrendingDown size={14} className="text-slate-500" />}
                    <span className="text-sm font-semibold text-slate-200">Lote {l.lote}</span>
                    {qtd > 0 && <span className="text-[11px] text-slate-500">{qtd} un</span>}
                    {pos != null && (
                      <span className={`text-[11px] px-1.5 py-0.5 rounded ${venceu ? 'bg-emerald-500/15 text-emerald-300' : 'bg-orange-500/15 text-orange-300'}`}>
                        {pos}º{l.total ? ` de ${l.total}` : ''}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-5 text-xs">
                    <div className="text-right">
                      <div className="text-slate-500 uppercase tracking-wide text-[10px]">Melhor</div>
                      <div className="font-mono text-slate-200">{fmtBRL4(melhorUnit)}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-slate-500 uppercase tracking-wide text-[10px]">Meu</div>
                      <div className={`font-mono ${venceu ? 'text-emerald-300' : 'text-slate-200'}`}>{fmtBRL4(meuUnit)}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-slate-500 uppercase tracking-wide text-[10px]">Piso</div>
                      <div className="font-mono text-slate-400">{fmtBRL4(pisoLote || null)}</div>
                    </div>
                    <div className="text-right min-w-[150px]">
                      <div className="text-slate-500 uppercase tracking-wide text-[10px]">Total do lote</div>
                      <div className="font-mono text-slate-300">
                        {meuUnit != null && qtd > 0 ? fmtBRL4(meuUnit * qtd) : '—'}
                      </div>
                    </div>
                  </div>
                </div>
                {atras && (
                  <div className="mt-1 text-[11px] text-amber-400">
                    ⚠ {fmtBRL4(meuUnit - melhorUnit)} acima do melhor{l.primeiroColocado ? ` — 1º: ${l.primeiroColocado}` : ''}
                  </div>
                )}
              </div>
            );
          })}
          {minhaGlobal && (
            <div className="text-[11px] text-slate-500">
              Somando os {lotesBll.length} lotes: <strong className="text-slate-300">{fmtBRL4(minhaGlobal.total)}</strong> — {minhaGlobal.position}º de {globalBll.length}.
              O portal não faz essa conta; ele mostra um 1º colocado por lote.
            </div>
          )}
        </div>
      )}

      {/*
        MESMO BLOCO, para os portais sem resultData.lotes (ComprasNet, PCP).
        Cobre os três formatos com o mesmo conceito de UNIDADE: grupo, item
        solto e misto — cada um com vencedor próprio.

        Motivador real: PE 90004/2026 (Fazenda Rio Grande) tem 3 GRUPOS e a
        IPCOM cotou só o grupo 2. A tela listava os 12 itens em sequência, com
        os grupos 1 e 3 aparecendo com "LIMITE 0,0000" sem dizer que não
        participamos — e o "Melhor possível" somava tudo.
      */}
      {!multiLote && multiUnidade && (
        <div className="mt-3 space-y-2">
          <div className="text-xs uppercase text-slate-500 tracking-wide">
            Por unidade de disputa
            <span className="normal-case text-slate-600 ml-2">
              {unidadesCotadas.length < unidades.length
                ? `cotamos ${unidadesCotadas.length} de ${unidades.length} — cada uma tem vencedor próprio`
                : 'cada uma tem vencedor próprio'}
              {isPcpGlobal && ' · valor global por lote'}
            </span>
          </div>
          {unidades.map((u) => {
            const venceu = u.posicao === 1;
            // Relógio DESTA unidade. O do topo mostra a próxima a encerrar, que
            // é o resumo certo pra decidir onde olhar — mas esconde que as
            // outras têm prazos diferentes, e cada uma prorroga sozinha.
            const restaMs = u.fimContagem != null ? u.fimContagem - now : null;
            const correndo = restaMs != null && restaMs > 0;
            return (
              <div
                key={u.chave}
                className={`rounded-lg border px-3 py-2.5 ${
                  u.naoCotada
                    ? 'bg-central-elevado border-slate-800 opacity-60'
                    : venceu
                      ? 'bg-emerald-500/5 border-emerald-700/50'
                      : 'bg-central-elevado border-central-borda'
                }`}
              >
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 min-w-[140px]">
                    {u.naoCotada
                      ? <span className="text-slate-600 text-xs">—</span>
                      : venceu
                        ? <Award size={14} className="text-amber-400" />
                        : <TrendingDown size={14} className="text-slate-500" />}
                    <span className={`text-sm font-semibold ${u.naoCotada ? 'text-slate-500' : 'text-slate-200'}`}>
                      {u.rotulo}
                    </span>
                    <span className="text-[11px] text-slate-500">
                      {u.itens.length} {u.itens.length === 1 ? 'item' : 'itens'}
                    </span>
                    {u.naoCotada ? (
                      <span
                        className="text-[11px] px-1.5 py-0.5 rounded bg-slate-700/50 text-slate-400"
                        title="Nenhum piso configurado nesta unidade — a IPCOM não disputou."
                      >
                        não participamos
                      </span>
                    ) : u.posicao != null && (
                      <span className={`text-[11px] px-1.5 py-0.5 rounded ${venceu ? 'bg-emerald-500/15 text-emerald-300' : 'bg-orange-500/15 text-orange-300'}`}>
                        {u.posicao}º{u.totalConcorrentes ? ` de ${u.totalConcorrentes}` : ''}
                      </span>
                    )}
                    {restaMs != null && (
                      correndo ? (
                        <span
                          className={`text-[11px] px-1.5 py-0.5 rounded font-mono font-bold tabular-nums flex items-center gap-1 ${
                            restaMs <= 60_000
                              ? 'bg-red-500/15 text-red-300'
                              : restaMs <= 120_000
                                ? 'bg-amber-500/15 text-amber-300'
                                : 'bg-slate-700/50 text-slate-300'
                          }`}
                          title="Tempo restante desta unidade — cada uma prorroga sozinha"
                        >
                          <Clock size={10} />
                          {formatCountdown(restaMs)}
                        </span>
                      ) : (
                        <span className="text-[11px] px-1.5 py-0.5 rounded bg-central-cartao text-slate-500" title="Contagem desta unidade encerrada">
                          encerrada
                        </span>
                      )
                    )}
                  </div>
                  {!u.naoCotada && (
                    <div className="flex items-center gap-5 text-xs">
                      <div className="text-right">
                        <div className="text-slate-500 uppercase tracking-wide text-[10px]">Melhor</div>
                        <div className="font-mono text-slate-200">{fmtBRL4(u.melhorTotal)}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-slate-500 uppercase tracking-wide text-[10px]">Meu</div>
                        <div className={`font-mono ${venceu ? 'text-emerald-300' : 'text-slate-200'}`}>{fmtBRL4(u.meuTotal)}</div>
                      </div>
                      <div className="text-right min-w-[110px]">
                        <div className="text-slate-500 uppercase tracking-wide text-[10px]">Piso da unidade</div>
                        <div className="font-mono text-slate-400">{fmtBRL4(u.piso)}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {unidadesCotadas.length < unidades.length && (
            <div className="text-[11px] text-slate-500">
              O "Melhor possível" acima considera só as {unidadesCotadas.length} {unidadesCotadas.length === 1 ? 'unidade cotada' : 'unidades cotadas'} — somar unidades que não disputamos daria um total que ninguém disputa.
            </div>
          )}
        </div>
      )}

      {/* Posição POR ITEM (disputa por item) — cada item tem ranking/vencedor próprios */}
      {isPerItemRanking && (
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs bg-central-elevado border border-slate-800 rounded-lg px-3 py-2">
          <span className="uppercase text-slate-500 tracking-wide">Posição por item:</span>
          {outcomesByItem.map((o) => (
            <span key={o.itemNumber} className={o.isWinner ? 'text-amber-400 font-semibold' : o.found ? 'text-slate-200' : 'text-slate-500'}>
              Item {o.itemNumber}: {o.classif != null ? `${o.classif}º${o.total ? ` de ${o.total}` : ''}` : '—'}{o.derived ? '*' : ''}
            </span>
          ))}
        </div>
      )}

      {/* Alert quando fase está em "Seleção" e existe negociação ativa */}
      {fase.key === 'selecao' && minhaProp?.situacaoUltimaNegociacao === 'E' && (
        <div className="mt-3 bg-amber-500/10 border border-amber-700 rounded-lg p-3 flex items-start gap-2">
          <AlertCircle size={16} className="text-amber-400 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-amber-200">
            <strong>Negociação ativa:</strong> o pregoeiro está convocando o(s) melhor(es) classificado(s) pra apresentar proposta mais vantajosa.
          </div>
        </div>
      )}

      {/* R1.11: Configurações da Sessão Pública — campos do endpoint /participacao */}
      {(() => {
        const part = session?.rankingData?.participacao;
        if (!part) return null;
        const cfg = part.configuracao || {};
        const fundamento = part.fundamentoLegal === 'L14133' ? 'Lei 14.133/2021' : (part.fundamentoLegal || '—');
        const criterio = ({
          1: 'Menor preço',
          2: 'Maior desconto',
          4: 'Técnica e preço',
          5: 'Maior lance',
          6: 'Melhor técnica',
          7: 'Conteúdo artístico',
        } as Record<number, string>)[Number(part.criterioJulgamento)] || part.criterioJulgamento || '—';
        const fmtDateLocal = (s: string | null | undefined) =>
          s ? new Date(s).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
        return (
          <div className="mt-4 pt-4 border-t border-central-borda">
            <div className="text-xs uppercase text-slate-500 tracking-wide mb-2 flex items-center gap-1.5">
              <AlertCircle size={12} />
              Configurações da Sessão Pública
            </div>
            {/* Linha 1: Datas críticas */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-xs mb-3">
              <div className="bg-central-elevado rounded p-2 border border-central-borda">
                <div className="text-[10px] uppercase text-slate-500">Propostas</div>
                <div className="text-slate-300 font-mono">{fmtDateLocal(part.dataHoraInicioPropostas)}</div>
                <div className="text-slate-500 font-mono text-[10px]">até {fmtDateLocal(part.dataHoraFimPropostas)}</div>
              </div>
              <div className="bg-central-elevado rounded p-2 border border-central-borda">
                <div className="text-[10px] uppercase text-slate-500">Abertura prevista</div>
                <div className="text-slate-300 font-mono">{fmtDateLocal(part.dataHoraPrevisaoAbertura)}</div>
                {part.dataHoraAbertura && (
                  <div className="text-emerald-400 font-mono text-[10px]">real {fmtDateLocal(part.dataHoraAbertura)}</div>
                )}
              </div>
              <div className="bg-central-elevado rounded p-2 border border-central-borda">
                <div className="text-[10px] uppercase text-slate-500">Disputa</div>
                <div className="text-slate-300 font-mono">{fmtDateLocal(part.dataHoraInicioDisputa)}</div>
                <div className="text-slate-500 font-mono text-[10px]">até {fmtDateLocal(part.dataHoraFimDisputa)}</div>
              </div>
              <div className="bg-central-elevado rounded p-2 border border-central-borda">
                <div className="text-[10px] uppercase text-slate-500">Janela abertura itens</div>
                <div className="text-slate-300 font-mono">{cfg.inicioPeriodoAberturaItem || '—'}{cfg.fimPeriodoAberturaItem ? ` → ${cfg.fimPeriodoAberturaItem}` : ''}</div>
                <div className="text-slate-500 text-[10px]">{cfg.qtdeMaximaItensEmDisputa ? `máx ${cfg.qtdeMaximaItensEmDisputa} simultâneos` : ''}</div>
              </div>
            </div>
            {/* Linha 2: Regras + declarações */}
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="px-2 py-0.5 rounded bg-central-cartao border border-central-borda text-slate-300">
                <span className="text-slate-500">Fundamento:</span> <span className="font-medium">{fundamento}</span>
              </span>
              <span className="px-2 py-0.5 rounded bg-central-cartao border border-central-borda text-slate-300">
                <span className="text-slate-500">Critério:</span> <span className="font-medium">{criterio}</span>
              </span>
              {cfg.analisePropostasAutomatica && (
                <span className="px-2 py-0.5 rounded bg-emerald-500/10 border border-emerald-700/40 text-emerald-300">
                  ✓ Análise automática
                </span>
              )}
              {cfg.tempoAvisoPrevioAberturaItens != null && (
                <span className="px-2 py-0.5 rounded bg-central-cartao border border-central-borda text-slate-300">
                  <span className="text-slate-500">Aviso prévio:</span> <span className="font-medium">{cfg.tempoAvisoPrevioAberturaItens} min</span>
                </span>
              )}
              {cfg.tempoIntencoesRecurso != null && (
                <span className="px-2 py-0.5 rounded bg-central-cartao border border-central-borda text-slate-300">
                  <span className="text-slate-500">Intenções de recurso:</span> <span className="font-medium">{cfg.tempoIntencoesRecurso} min</span>
                </span>
              )}
              {part.exigeDeclaracaoProgramasIntegridade && (
                <span className="px-2 py-0.5 rounded bg-amber-500/10 border border-amber-700/40 text-amber-300" title="Declaração de Programas de Integridade obrigatória">
                  ⚠ Decl. Integridade
                </span>
              )}
              {part.exigeDeclaracaoEquidadeGenero && (
                <span className="px-2 py-0.5 rounded bg-amber-500/10 border border-amber-700/40 text-amber-300" title="Declaração de Equidade de Gênero obrigatória">
                  ⚠ Decl. Equidade
                </span>
              )}
              {part.desconsiderarDesempatePorUf && (
                <span className="px-2 py-0.5 rounded bg-central-cartao border border-central-borda text-slate-400 text-[11px]">
                  Sem desempate por UF
                </span>
              )}
              {part.linkPncp && (
                <a href={part.linkPncp} target="_blank" rel="noopener noreferrer"
                  className="px-2 py-0.5 rounded bg-blue-500/10 border border-blue-700/40 text-blue-300 hover:bg-blue-500/20"
                  onClick={(e) => e.stopPropagation()}>
                  Edital PNCP ↗
                </a>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
